import { ensureInt } from "@/helpers/utility/Type";
import { toUid } from "@/helpers/utility/Id";
import type { Uid } from "@/helpers/utility/Id";
import type { ImpulseItem } from "../impulse/ImpulseItem";
import type { SimulationRoundItem } from "./SimulationRoundItem";
import type { DeepReadonly } from "vue";
import type { RelationItem } from "../relation/RelationItem";

/**
 * Get all impulses that were created directly or indirectly
 * because of a manual effect on the net.
 * (sum of all values included.)
 * @param rounds
 * @param effectUse
 */
export function collectEffectConsequences(
  rounds: DeepReadonly<SimulationRoundItem[]>,
  roundIndices: Set<number>,
  effectUse: {
    type: "action" | "incident" | "scheduledEffect";
    id: number | string;
    roundIndex: number;
  },
  outgoingRelationsByElement: Record<Uid<string>, RelationItem[]>
) {
  const activatorsByRound: {
    [roundIndex: number]: {
      [type: string]: {
        [id: number | string]: {
          roundIndex?: number; // The round the activator was activated in
          activatorType?: "target" | "resource" | "action" | "incident" | "scheduledEffect"; // The type of the activator
          activatorId?: number | string; // The id of the activator
          influence: number; // How much of the value created by the activator should be considered?
        };
      };
    };
  } = {
    [effectUse.roundIndex]: {
      [effectUse.type]: {
        [effectUse.id]: {
          influence: 1,
          roundIndex: effectUse.roundIndex,
          activatorType: effectUse.type,
          activatorId: effectUse.id
        }
      }
    }
  };

  const totals = {
    total: 0,
    target: {} as Record<number, number>,
    resource: {} as Record<number, number>
  };

  const consequencesByRound: {
    [roundIndex: number]: (ImpulseItem & {
      affectedId: number;
      affectedType: "target" | "resource";
      roundIndex: number;
      activators: { id: string; value: number }[];
    })[];
  } = {};

  /**
   * For each round, check if there are any impulses that were
   * created by activators that were activated in the current
   * round or earlier.
   */
  for (const round of rounds) {
    if (round.index < effectUse.roundIndex) continue;

    const activators = activatorsByRound[round.index] || {};

    consequencesByRound[round.index] = [];

    // For each impulse in the current round, check if there are
    // any activators that were activated in the current round
    // or earlier.
    for (const type in round.impulses) {
      const itemType = type as "target" | "resource";
      const impulses = round.impulses[itemType];
      for (const itemId in impulses) {
        const impulse = impulses[itemId];

        // Check if there are any activators that were activated
        // in the current round or earlier.
        const taintedSources = impulse.sourceValues.filter((sourceValue) => {
          const isActivated =
            sourceValue.type in activators &&
            sourceValue.id in activators[sourceValue.type];

          if (!isActivated) return false;

          const activator = activators[sourceValue.type][sourceValue.id];

          return Math.abs((sourceValue.value || 0) * activator.influence) >= 0.01;
        });

        if (taintedSources.length == 0) continue;

        const impulseActivators: { id: string; value: number }[] = [];

        const totalValue = taintedSources.reduce((sum, source) => {
          const activator = activators[source.type][source.id];
          const partValue = (source.value || 0) * activator.influence;
          impulseActivators.push({
            id: `${activator.roundIndex}_${activator.activatorType}_${activator.activatorId}`,
            value: partValue
          });
          // we're only adding in the amount of the value that
          // corresponds to the value of the original impulse
          // in relation to the current impulse value.
          return sum + partValue;
        }, 0);

        // add the value of the impulse to the total impact value
        // the effect made in the net
        if (roundIndices.has(round.index)) {
          totals.total += totalValue;
          totals[itemType][itemId] =
            (totals[itemType]?.[itemId] || 0) + totalValue;
        }

        // Clamp the min influence to 0...1.
        // This makes sure that we do not attribute influence
        // on an impulse in which the effect was negated.
        const percentageInfluenced =
          Math.sign(totalValue) == Math.sign((impulse.pointsToAdd || 0))
            ? Math.min(1, Math.max(0, totalValue / (impulse.pointsToAdd || 0)))
            : 0;

        // build a new impulse including only the affected values
        // and add it to the collection.
        if (roundIndices.has(round.index)) {
          consequencesByRound[round.index].push({
            type: "impulse",
            roundIndex: round.index,
            affectedType: type as "target" | "resource",
            affectedId: ensureInt(itemId),
            activators: impulseActivators,
            pointsToAdd: totalValue,
            sourceValues: taintedSources
          });
        }

        const itemUid = toUid(itemType, itemId);

        // add all outgoing relations as new activators
        if (Math.abs(totalValue) > 0.001 && type == "target") {
          const outgoingRelations = outgoingRelationsByElement[itemUid] ?? [];
          //  useRelationStore().activeByStartUid[itemUid] ?? [];
          for (const relation of outgoingRelations) {
            const targetRoundIndex = round.index + 1 + (relation.delay || 0);
            if (!(targetRoundIndex in activatorsByRound))
              activatorsByRound[targetRoundIndex] = { relation: {} };
            if (!("relation" in activatorsByRound[targetRoundIndex]))
              activatorsByRound[targetRoundIndex].relation = {};
            activatorsByRound[targetRoundIndex].relation[relation.id] = {
              influence: percentageInfluenced,
              roundIndex: round.index,
              activatorType: type as "target",
              activatorId: ensureInt(itemId)
            };
          }
        }
      }
    }
  }

  return { consequencesByRound, ...totals };
}
